/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 11/07/2025 15:57
 *
 */

import { Action, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  MapTabsAddIdsAction,
  MapTabsClearAction,
  MapTabsCloseAction,
  MapTabsCloseByPresetIdAction,
  MapTabsRemoveIdsAction,
  MapTabsSelectAction,
  MapTabsSetCurrentAction,
  MapTabsSetCurrentResourceTypeAction,
  MapTabsSetDisplayModeAction,
  MapTabsSetFilterAction,
  MapTabsSetIdsAction,
  MapTabsSetSearchAction,
  MapTabsUpdatePresetAction,
} from '../actions/map-tabs.actions';
import { CartographyTabV2Dto } from '../../module/cartography/types/cartography-tab-v2/cartography-tab-v2.dto';
import { CartographyResourceTypeEnum } from '../../module/cartography/enum/cartography-resource-type.enum';
import { Tools } from '@monorepo-projects/inf-commons-lib';

// Todo:: Work with object

export class MapTabsStateModel {
  currentTab: CartographyTabV2Dto | undefined;
  tabs: CartographyTabV2Dto[] = [];
}

@State<MapTabsStateModel>({
  name: 'mapTabs',
  defaults: {
    currentTab: null,
    tabs: [],
  },
})
@Injectable()
export class MapTabsState {
  @Action(MapTabsClearAction)
  clearTabs(ctx: StateContext<MapTabsStateModel>) {
    ctx.patchState({
      currentTab: null,
    });
  }

  @Action(MapTabsSetCurrentAction)
  setCurrentTab(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsSetCurrentAction,
  ) {
    const tabs = ctx.getState().tabs;
    if (tabs.find((t) => t.id === action.tab.id)) {
      ctx.patchState({
        currentTab: action.tab,
      });
      return;
    }
    ctx.patchState({
      currentTab: action.tab,
      tabs: [...tabs, action.tab],
    });
  }

  @Action(MapTabsSelectAction)
  select(ctx: StateContext<MapTabsStateModel>, action: MapTabsSelectAction) {
    const { id } = action;
    const tabs = ctx.getState().tabs;
    const selectedTab = tabs.find((t) => t.id === id);
    if (selectedTab) {
      ctx.patchState({
        currentTab: selectedTab,
      });
      return;
    }
    console.warn(`Tab with id ${id} not found.`);
  }

  @Action(MapTabsCloseAction)
  close(ctx: StateContext<MapTabsStateModel>, action: MapTabsCloseAction) {
    const { id } = action;
    const tabs = [...ctx.getState().tabs];
    const existingTabIndex = tabs.findIndex((t) => t.id === id);
    if (existingTabIndex > -1) {
      tabs.splice(existingTabIndex, 1);
    }
    ctx.patchState({
      tabs,
    });
  }

  @Action(MapTabsAddIdsAction)
  addIds(ctx: StateContext<MapTabsStateModel>, action: MapTabsAddIdsAction) {
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to add IDs to.');
      return;
    }
    const newTab = { ...currentTab };
    switch (action.type) {
      case CartographyResourceTypeEnum.bbx:
        newTab.selectedBbxIds = Array.from(
          new Set([...newTab.selectedBbxIds, ...action.ids]),
        );
        break;
      case CartographyResourceTypeEnum.tag:
        newTab.selectedMatIds = Array.from(
          new Set([...newTab.selectedMatIds, ...action.ids]),
        );
        break;
      case CartographyResourceTypeEnum.site:
        newTab.selectedSiteIds = Array.from(
          new Set([...newTab.selectedSiteIds, ...action.ids]),
        );
        break;
    }
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsRemoveIdsAction)
  removeIds(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsRemoveIdsAction,
  ) {
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to remove IDs from.');
      return;
    }
    const newTab = { ...currentTab };
    switch (action.type) {
      case CartographyResourceTypeEnum.bbx:
        newTab.selectedBbxIds = Array.from(
          new Set(
            newTab.selectedBbxIds.filter((id) => !action.ids.includes(id)),
          ),
        );
        break;
      case CartographyResourceTypeEnum.tag:
        newTab.selectedMatIds = Array.from(
          new Set(
            newTab.selectedMatIds.filter((id) => !action.ids.includes(id)),
          ),
        );
        break;
      case CartographyResourceTypeEnum.site:
        newTab.selectedSiteIds = Array.from(
          new Set(
            newTab.selectedSiteIds.filter((id) => !action.ids.includes(id)),
          ),
        );
        break;
    }
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsSetIdsAction)
  setIds(ctx: StateContext<MapTabsStateModel>, action: MapTabsSetIdsAction) {
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to set IDs for.');
      return;
    }
    const newTab = { ...currentTab };
    switch (action.type) {
      case CartographyResourceTypeEnum.bbx:
        newTab.selectedBbxIds = Array.from(new Set(action.ids));
        break;
      case CartographyResourceTypeEnum.tag:
        newTab.selectedMatIds = Array.from(new Set(action.ids));
        break;
      case CartographyResourceTypeEnum.site:
        newTab.selectedSiteIds = Array.from(new Set(action.ids));
        break;
    }
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsSetSearchAction)
  setSearch(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsSetSearchAction,
  ) {
    const { search, type } = action;
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to set search for.');
      return;
    }
    const newTab = { ...currentTab };
    switch (type) {
      case CartographyResourceTypeEnum.bbx: {
        newTab.searchTextBbx = search;
        break;
      }
      case CartographyResourceTypeEnum.tag: {
        newTab.searchTextMat = search;
        break;
      }
      case CartographyResourceTypeEnum.site: {
        newTab.searchTextSite = search;
        break;
      }
    }
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsSetFilterAction)
  setFilter(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsSetFilterAction,
  ) {
    const { filter } = action;
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to set search for.');
      return;
    }
    const newTab = { ...currentTab };
    newTab.statusBbx = filter;
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsSetDisplayModeAction)
  setDisplayMode(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsSetDisplayModeAction,
  ) {
    const { displayMode } = action;
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to set search for.');
      return;
    }
    const newTab = { ...currentTab };
    newTab.displayMode = displayMode;
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsSetCurrentResourceTypeAction)
  setCurrentResourceType(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsSetCurrentResourceTypeAction,
  ) {
    const { resourceType } = action;
    const currentTab = ctx.getState().currentTab;
    if (!currentTab) {
      console.warn('No current tab to set search for.');
      return;
    }
    const newTab = { ...currentTab };
    newTab.currentResourceType = resourceType;
    const tabs = this.updateTabs(ctx, newTab);
    ctx.patchState({
      currentTab: newTab,
      tabs,
    });
  }

  @Action(MapTabsCloseByPresetIdAction)
  closeByPresetId(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsCloseByPresetIdAction,
  ) {
    const { presetId } = action;
    const tabs = ctx
      .getState()
      .tabs.filter((tab) => tab.preset?.id !== presetId);
    ctx.patchState({
      tabs,
    });
  }

  @Action(MapTabsUpdatePresetAction)
  updatePreset(
    ctx: StateContext<MapTabsStateModel>,
    action: MapTabsUpdatePresetAction,
  ) {
    const { preset } = action;
    const tabIndex = ctx
      .getState()
      .tabs.findIndex((t) => t.preset?.id === preset.id);
    if (tabIndex !== -1) {
      const tabToUpdate = ctx.getState().tabs[tabIndex];

      const selectedBbxIds = Tools.applyDiffIds(
        tabToUpdate.preset.selectedBbxId,
        preset.selectedBbxId,
        tabToUpdate.selectedBbxIds ?? [],
      );
      const selectedTagIds = Tools.applyDiffIds(
        tabToUpdate.preset.selectedMaterialsId,
        preset.selectedMaterialsId,
        tabToUpdate.selectedMatIds ?? [],
      );
      const selectedSiteIds = Tools.applyDiffIds(
        tabToUpdate.preset.selectedSitesId,
        preset.selectedSitesId,
        tabToUpdate.selectedSiteIds ?? [],
      );

      const updatedTab = { ...tabToUpdate, preset };
      updatedTab.label = preset.label;
      updatedTab.selectedBbxIds = selectedBbxIds;
      updatedTab.selectedMatIds = selectedTagIds;
      updatedTab.selectedSiteIds = selectedSiteIds;

      const tabs = this.updateTabs(ctx, updatedTab);
      ctx.patchState({
        tabs,
      });
    }
  }

  private updateTabs(
    ctx: StateContext<MapTabsStateModel>,
    newTab: CartographyTabV2Dto,
  ): CartographyTabV2Dto[] {
    const tabs = [...ctx.getState().tabs];
    const existingTabIndex = tabs.findIndex((t) => t.id === newTab.id);
    if (existingTabIndex !== -1) {
      tabs[existingTabIndex] = newTab;
    } else {
      tabs.push(newTab);
    }
    return tabs;
  }
}
